// ============================================================================
// ENGAGEMENT TYPE FORM MODAL
// ============================================================================
// Add / Edit form modal for the EngagementType master.
//
// Follows the prima-web mandatory form-modal blueprint (see CLAUDE.md):
//  - Internal formData state with refs for stable callbacks
//  - Initial snapshot for unsaved-changes (dirty) tracking
//  - Inline field validation via fieldErrors + getFieldError + clearFieldError
//  - 3-stage save flow: validate → ConfirmDialog → loading → toast
//  - Discard-changes ConfirmDialog on close when isDirty
//  - Loading overlay during save (uses var(--modal-loading-overlay))
//  - Form control borders use var(--form-border-color), error class on invalid
//  - Minimize-to-bar support (chevronDown header button) — multi-instance Add via createInstanceId
//
// Field set mirrors prima-api CreateEngagementTypeRequest:
//   code         [Required, max 30, uppercase by convention]
//   name         [Required, max 100]
//   aliasName    [optional, max 100]
//   description  [optional]
//   statusCode   ("ACTIVE" or "INACTIVE")

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

export interface EngagementTypeFormData {
  code: string;
  name: string;
  aliasName: string;
  description: string;
  statusCode: 'ACTIVE' | 'INACTIVE';
}

export interface EngagementTypeFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  /** Existing record (edit mode). Null/undefined in create mode. */
  initialData?: EngagementTypeFormData | null;
  /** Saving flag from parent — controls loading overlay + button disable */
  isSaving?: boolean;
  /** Optional API error message to surface in the modal */
  apiError?: string | null;
  onSave: (data: EngagementTypeFormData) => void;
  onClose: () => void;
  // ---- Minimize-to-bar support ----
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const INITIAL_FORM_DATA: EngagementTypeFormData = {
  code: '',
  name: '',
  aliasName: '',
  description: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

// Code pattern: letters, numbers, underscores, hyphens (matches old prima UX)
const CODE_PATTERN = /^[A-Z0-9_-]+$/;

export const EngagementTypeFormModal: React.FC<EngagementTypeFormModalProps> = ({
  isOpen,
  mode,
  initialData,
  isSaving = false,
  apiError,
  onSave,
  onClose,
  tabId,
  moduleName,
  onRestoreModal,
  createInstanceId,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<EngagementTypeFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  // When true, the next isOpen=true is a restore — skip form reset to preserve user edits
  const isRestoreRef = useRef(false);

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback(
    (field: string) => fieldErrors[field],
    [fieldErrors]
  );
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // --- Populate / reset form on open. Skip on restore. ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const src = initialDataRef.current;
    const data: EngagementTypeFormData = src
      ? {
          code: src.code ?? '',
          name: src.name ?? '',
          aliasName: src.aliasName ?? '',
          description: src.description ?? '',
          statusCode: (src.statusCode as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE',
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Dirty tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler (checks dirty state) ---
  const handleClose = useCallback(() => {
    if (isSaving) return; // Prevent close during save
    if (isDirty) {
      setDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose, isSaving]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentFormData.code || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.code?.trim();
      const name = currentFormData.name?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.code || 'Engagement Type'}`;
    }

    // Capture this session's data + snapshot in the closure
    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentFormData.code : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  // Expose handleMinimize to parent for programmatic auto-minimize
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Validate then show save confirmation ---
  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.code.trim();
    const name = formData.name.trim();

    if (!code) {
      errors.code = 'Code is required';
    } else if (code.length > 30) {
      errors.code = 'Code must be 30 characters or less';
    } else if (!CODE_PATTERN.test(code)) {
      errors.code = 'Code can only contain uppercase letters, numbers, hyphens, and underscores';
    }

    if (!name) {
      errors.name = 'Name is required';
    } else if (name.length > 100) {
      errors.name = 'Name must be 100 characters or less';
    }

    if (formData.aliasName && formData.aliasName.length > 100) {
      errors.aliasName = 'Alias name must be 100 characters or less';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Scroll first error into view
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(
          `[data-field="${firstField}"]`
        );
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input, textarea')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // --- Confirmed save → delegate to parent ---
  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    onSave({
      ...formData,
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
      aliasName: formData.aliasName.trim(),
      description: formData.description.trim(),
    });
  }, [formData, onSave]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {/* Loading overlay during save — position: absolute works because parent is position: fixed */}
        {isSaving && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span
              style={{
                fontSize: '0.85rem',
                color: 'var(--text-secondary)',
                fontWeight: 500,
              }}
            >
              Saving...
            </span>
          </div>
        )}

        {/* Header */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add New Engagement Type' : 'Edit Engagement Type'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button
              className="modal-close"
              onClick={handleClose}
              disabled={isSaving}
              title="Close"
            >
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {apiError && (
            <div
              style={{
                marginBottom: '0.75rem',
                padding: '0.625rem 0.75rem',
                background: 'rgba(220, 38, 38, 0.08)',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                color: '#dc2626',
                fontSize: '0.8125rem',
                lineHeight: 1.4,
              }}
            >
              {apiError}
            </div>
          )}

          <div className="form-grid-2">
            {/* Code */}
            <div className="form-group" data-field="code">
              <label className="form-label">
                Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${getFieldError('code') ? ' form-field-error' : ''}`}
                value={formData.code}
                maxLength={30}
                placeholder="e.g. AUDIT"
                disabled={isSaving || mode === 'edit'}
                onChange={(e) => {
                  clearFieldError('code');
                  setFormData((prev) => ({
                    ...prev,
                    code: e.target.value.toUpperCase(),
                  }));
                }}
              />
              {getFieldError('code') && (
                <div className="form-error-message">{getFieldError('code')}</div>
              )}
            </div>

            {/* Status */}
            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                value={formData.statusCode}
                options={STATUS_OPTIONS}
                isClearable={false}
                size="sm"
                onChange={(val) => {
                  clearFieldError('statusCode');
                  setFormData((prev) => ({
                    ...prev,
                    statusCode: (val as 'ACTIVE' | 'INACTIVE') || 'ACTIVE',
                  }));
                }}
              />
            </div>
          </div>

          {/* Name */}
          <div className="form-group" data-field="name">
            <label className="form-label">
              Engagement Type Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('name') ? ' form-field-error' : ''}`}
              value={formData.name}
              maxLength={100}
              placeholder="e.g. Statutory Audit"
              disabled={isSaving}
              onChange={(e) => {
                clearFieldError('name');
                setFormData((prev) => ({ ...prev, name: e.target.value }));
              }}
            />
            {getFieldError('name') && (
              <div className="form-error-message">{getFieldError('name')}</div>
            )}
          </div>

          {/* Alias name */}
          <div className="form-group" data-field="aliasName">
            <label className="form-label">Alias Name</label>
            <input
              type="text"
              className={`form-input${getFieldError('aliasName') ? ' form-field-error' : ''}`}
              value={formData.aliasName}
              maxLength={100}
              placeholder="Optional short name"
              disabled={isSaving}
              onChange={(e) => {
                clearFieldError('aliasName');
                setFormData((prev) => ({ ...prev, aliasName: e.target.value }));
              }}
            />
            {getFieldError('aliasName') && (
              <div className="form-error-message">{getFieldError('aliasName')}</div>
            )}
          </div>

          {/* Description */}
          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.description}
              rows={3}
              placeholder="Optional description"
              disabled={isSaving}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, description: e.target.value }));
              }}
              style={{ resize: 'vertical' }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          mode === 'create'
            ? `Create engagement type "${formData.code.trim().toUpperCase()}"?`
            : `Save changes to "${formData.code}"?`
        }
        confirmLabel={mode === 'create' ? 'Create' : 'Save'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default EngagementTypeFormModal;
